import { AsyncPipe } from '@angular/common';
import { AfterViewInit, Component, computed, inject, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { Params, RouterOutlet } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe } from '@ngx-translate/core';
import { StickyBarComponent, tryToUnsubscribeFromSubscription } from '@swissfex/sfx-commons';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { Observable, Subscription } from 'rxjs';

import { NewsBarComponent } from '../common-components/news/news-bar.component';
import { SessionStatusNotificationComponent } from '../common-components/session-status-notification/session-status-notification.component';
import { SidebarNavigationComponent } from '../common-components/sidebar-navigation/sidebar-navigation.component';
import { OUTLET_NAMES } from '../constants';
import { IApplicationUser } from '../core/models/IApplicationUser';
import { RealTimeNotificationService } from '../core/services/real-time-notification.service';
import { SESSION_STORAGE_KEY, SessionStorageService } from '../core/services/session-storage.service';
import { INews } from '../projects/models/INews';
import { AuthenticationService } from '../projects/services/authentication.service';
import { AuthorizationService } from '../projects/services/authorization.service';
import { NewsService } from '../projects/services/news.service';

@UntilDestroy({ checkProperties: true })
@Component({
    templateUrl: './authorized-root.component.html',
    styleUrl: './authorized-root.component.scss',
    imports: [
        NewsBarComponent,
        SidebarNavigationComponent,
        RouterOutlet,
        SessionStatusNotificationComponent,
        AsyncPipe,
        TranslatePipe,
        StickyBarComponent
    ]
})
export class AuthorizedRootComponent implements OnInit, AfterViewInit, OnDestroy {
    private readonly authenticationSvc = inject(AuthenticationService);
    private readonly authorizationSvc = inject(AuthorizationService);
    private readonly newsSvc = inject(NewsService);
    private readonly nzNotificationSvc = inject(NzNotificationService);
    private readonly rtNotificationSvc = inject(RealTimeNotificationService);
    private readonly sessionStorageSvc = inject(SessionStorageService);

    public readonly currentUser$: Observable<IApplicationUser>;
    public news = signal<INews[]>([]);
    public shouldDisplayAdminBanner = computed<boolean>(() => this.isBackofficeUser() && this.displayAdminBanner());
    public projectId = signal<number>(null);

    protected readonly OUTLET_NAMES = OUTLET_NAMES;
    private displayAdminBanner = signal<boolean>(true);
    private isBackofficeUser = signal<boolean>(false);
    private routerOutletActivatedRouteParamsSubscription: Subscription;

    public routerOutlet = viewChild<RouterOutlet>(RouterOutlet);

    constructor() {
        this.sessionStorageSvc.remove(SESSION_STORAGE_KEY.RETURN_URL_KEY);
        this.currentUser$ = this.authenticationSvc.currentUser$;
    }

    public async ngOnInit(): Promise<void> {
        this.displayAdminBanner.set(!this.sessionStorageSvc.get(SESSION_STORAGE_KEY.ADMIN_BANNER_HIDDEN));
        this.isBackofficeUser.set(this.authorizationSvc.isSfxAdmin);
        await this.processLoggedInImplications();
    }

    public ngAfterViewInit(): void {
        this.subscribeToRouterOutletActivatedRouteParams();
    }

    public async ngOnDestroy(): Promise<void> {
        await this.rtNotificationSvc.disconnect();
    }

    public onAdminBannerClicked(): void {
        this.displayAdminBanner.set(false);
        this.sessionStorageSvc.set(SESSION_STORAGE_KEY.ADMIN_BANNER_HIDDEN, true);
    }

    public subscribeToRouterOutletActivatedRouteParams(): void {
        tryToUnsubscribeFromSubscription(this.routerOutletActivatedRouteParamsSubscription);
        const routerOutletActivatedRoute = this.routerOutlet()?.activatedRoute;
        if (routerOutletActivatedRoute) {
            this.routerOutletActivatedRouteParamsSubscription = routerOutletActivatedRoute.params
                .pipe(untilDestroyed(this))
                .subscribe((params: Params) => {
                    this.projectId.set(params?.id ? +params.id : null);
                });
        }
    }

    private async processLoggedInImplications(): Promise<void> {
        await this.rtNotificationSvc.connect();
        this.rtNotificationSvc.messageNotifications.pipe(untilDestroyed(this)).subscribe((messageNotification) => {
            // TODO: remove when not necessary for illustrating the functionality
            const { projectId, channel, message, sender, dateTime } = messageNotification.payload;
            const title = `${projectId} - ${channel}`;
            const body = `${sender}: ${message} @ ${dateTime}`;
            this.nzNotificationSvc.create('info', title, body);
        });

        await this.getNews();
    }

    private async getNews(): Promise<void> {
        try {
            this.news.set(await this.newsSvc.getNews());
        } catch (e) {
            console.error(e);
        }
    }
}
